import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { parsePhone } from '@nine80/shared';
import type { PublicBarberDTO, RegisterResponseDTO, ShopStatusDTO } from '@nine80/shared';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { BarberSelect } from './BarberSelect';
import { PhoneInput } from './PhoneInput';
import { ErrorBanner, Spinner } from './Primitives';
import { ShopStatusBadge } from './ShopStatusBadge';

type Step = 'phone' | 'new' | 'returning' | 'confirmed';

export interface CheckInFormHandle {
  reset: () => void;
  /** True once the customer has typed anything — used to drive the kiosk inactivity timer. */
  hasStartedInput: () => boolean;
}

export const CheckInForm = forwardRef<
  CheckInFormHandle,
  {
    source: 'WEB' | 'KIOSK';
    onSuccess?: (result: RegisterResponseDTO) => void;
    autoFocusPhone?: boolean;
  }
>(function CheckInForm({ source, onSuccess, autoFocusPhone = true }, ref) {
  const { t, lang } = useI18n();
  const [shopStatus, setShopStatus] = useState<ShopStatusDTO | null>(null);
  const [barbers, setBarbers] = useState<PublicBarberDTO[]>([]);
  const [loadingContext, setLoadingContext] = useState(true);

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [returningFirstName, setReturningFirstName] = useState<string | null>(null);
  const [showUpdateInfo, setShowUpdateInfo] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [barberId, setBarberId] = useState<number | null>(null);
  const [honeypot, setHoneypot] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<RegisterResponseDTO | null>(null);

  const phoneInputId = 'checkin-phone';

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [status, barberRes] = await Promise.all([api.shopStatus(), api.barbers()]);
        if (!cancelled) {
          setShopStatus(status);
          setBarbers(barberRes.barbers);
        }
      } finally {
        if (!cancelled) setLoadingContext(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handlePhoneSubmit(e: FormEvent) {
    e.preventDefault();
    setPhoneError(null);
    const parsed = parsePhone(phone);
    if (!parsed.valid) {
      setPhoneError(t('checkIn.errorInvalidPhone'));
      return;
    }
    setLookupLoading(true);
    try {
      const res = await api.lookup(phone);
      if (res.found) {
        setReturningFirstName(res.firstName ?? null);
        setStep('returning');
      } else {
        setStep('new');
      }
    } catch {
      setPhoneError(t('common.networkError'));
    } finally {
      setLookupLoading(false);
    }
  }

  function resetAll() {
    setStep('phone');
    setPhone('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setMarketingConsent(false);
    setBarberId(null);
    setHoneypot('');
    setResult(null);
    setSubmitError(null);
    setShowUpdateInfo(false);
    setReturningFirstName(null);
  }

  const submittingRef = useRef(false);

  useImperativeHandle(ref, () => ({
    reset: resetAll,
    hasStartedInput: () => step !== 'phone' || phone.length > 0,
  }));

  async function handleNewSubmit(e: FormEvent) {
    e.preventDefault();
    if (submittingRef.current) return; // guards double-tap in addition to the disabled button
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await api.register({
        phone,
        firstName,
        lastName,
        email,
        requestedBarberId: barberId,
        marketingConsent,
        source,
        language: lang,
        website: honeypot,
      });
      setResult(res);
      setStep('confirmed');
      onSuccess?.(res);
    } catch (err) {
      handleSubmitError(err);
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  }

  async function handleReturningSubmit(e: FormEvent) {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await api.registerReturning({
        phone,
        requestedBarberId: barberId,
        source,
        language: lang,
        website: honeypot,
        updatedFirstName: showUpdateInfo && firstName ? firstName : undefined,
        updatedLastName: showUpdateInfo && lastName ? lastName : undefined,
        updatedEmail: showUpdateInfo && email ? email : undefined,
      });
      setResult(res);
      setStep('confirmed');
      onSuccess?.(res);
    } catch (err) {
      handleSubmitError(err);
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  }

  function handleSubmitError(err: unknown) {
    if (err instanceof ApiError) {
      if (err.code === 'already_on_list' && err.token) {
        window.location.href = `/status/${err.token}`;
        return;
      }
      const map: Record<string, string> = {
        invalid_phone: t('checkIn.errorInvalidPhone'),
        invalid_email: t('checkIn.errorInvalidEmail'),
        invalid_request: t('checkIn.errorInvalidName'),
        queue_closed: t('checkIn.errorQueueClosed'),
        barber_unavailable: t('checkIn.errorBarberUnavailable'),
        network_error: t('common.networkError'),
      };
      setSubmitError(map[err.code ?? ''] ?? err.message);
    } else {
      setSubmitError(t('common.networkError'));
    }
  }

  if (loadingContext) {
    return (
      <div className="flex justify-center py-12 text-fg-muted">
        <Spinner />
      </div>
    );
  }

  if (shopStatus && !shopStatus.acceptingWalkins) {
    return (
      <div className="card space-y-4">
        <ShopStatusBadge status={shopStatus} t={t} />
        <p className="text-fg-muted">{shopStatus.message ?? t('checkIn.errorQueueClosed')}</p>
      </div>
    );
  }

  if (step === 'confirmed' && result) {
    return (
      <div className="card space-y-4 text-center">
        <h2 className="text-2xl text-primary">{t('checkIn.confirmationTitle')}</h2>
        <p className="text-lg">{result.firstName}</p>
        <p className="text-fg-muted">
          {t('checkIn.confirmationRequested', {
            barber: result.requestedBarberName ?? t('barbers.nextAvailable'),
          })}
        </p>
        <p className="text-fg-muted">
          {result.eligibleAhead === 1
            ? t('checkIn.confirmationAheadSingular', { count: result.eligibleAhead })
            : t('checkIn.confirmationAhead', { count: result.eligibleAhead })}
        </p>
        {source === 'WEB' && (
          <>
            <p className="text-sm text-fg-muted">{t('checkIn.confirmationRemoteHint')}</p>
            <a className="btn-primary w-full" href={`/status/${result.token}`}>
              {t('checkIn.confirmationViewStatus')}
            </a>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="card space-y-5">
      {shopStatus && <ShopStatusBadge status={shopStatus} t={t} />}

      {step === 'phone' && (
        <form onSubmit={handlePhoneSubmit} className="space-y-4" noValidate>
          <div>
            <label htmlFor={phoneInputId} className="label">
              {t('checkIn.phoneLabel')}
            </label>
            <PhoneInput
              id={phoneInputId}
              value={phone}
              onChange={setPhone}
              placeholder={t('checkIn.phonePlaceholder')}
              autoFocus={autoFocusPhone}
            />
            {phoneError && <p className="mt-2 text-sm text-danger">{phoneError}</p>}
          </div>
          <button type="submit" className="btn-primary w-full" disabled={lookupLoading}>
            {lookupLoading ? <Spinner /> : t('checkIn.lookupButton')}
          </button>
        </form>
      )}

      {step === 'returning' && (
        <form onSubmit={handleReturningSubmit} className="space-y-4" noValidate>
          <p className="text-lg">{t('checkIn.welcomeBack', { name: returningFirstName ?? '' })}</p>
          <button
            type="button"
            className="text-sm text-primary underline"
            onClick={() => setStep('phone')}
          >
            {t('checkIn.notYou')}
          </button>

          <fieldset>
            <legend className="label" id="barber-label-returning">
              {t('barbers.label')}
            </legend>
            {barbers.length === 0 ? (
              <p className="text-sm text-warning">{t('barbers.noneAvailable')}</p>
            ) : (
              <BarberSelect
                barbers={barbers}
                value={barberId}
                onChange={setBarberId}
                nextAvailableLabel={t('barbers.nextAvailable')}
                labelledBy="barber-label-returning"
              />
            )}
          </fieldset>

          <button
            type="button"
            className="text-sm text-primary underline"
            onClick={() => setShowUpdateInfo((v) => !v)}
          >
            {t('checkIn.updateInfo')}
          </button>

          {showUpdateInfo && (
            <div className="space-y-3 rounded-card border border-border p-3">
              <div>
                <label className="label" htmlFor="upd-first">
                  {t('checkIn.firstNameLabel')}
                </label>
                <input
                  id="upd-first"
                  className="input"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="upd-last">
                  {t('checkIn.lastNameLabel')}
                </label>
                <input
                  id="upd-last"
                  className="input"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              <div>
                <label className="label" htmlFor="upd-email">
                  {t('checkIn.emailLabel')}
                </label>
                <input
                  id="upd-email"
                  className="input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
          )}

          <HoneypotField value={honeypot} onChange={setHoneypot} />

          {submitError && <ErrorBanner>{submitError}</ErrorBanner>}

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? <Spinner /> : t('checkIn.submitButton')}
          </button>
        </form>
      )}

      {step === 'new' && (
        <form onSubmit={handleNewSubmit} className="space-y-4" noValidate>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="new-first">
                {t('checkIn.firstNameLabel')}
              </label>
              <input
                id="new-first"
                className="input"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
              />
            </div>
            <div>
              <label className="label" htmlFor="new-last">
                {t('checkIn.lastNameLabel')}
              </label>
              <input
                id="new-last"
                className="input"
                required
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
              />
            </div>
          </div>
          <div>
            <label className="label" htmlFor="new-email">
              {t('checkIn.emailLabel')}
            </label>
            <input
              id="new-email"
              className="input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>

          <fieldset>
            <legend className="label" id="barber-label-new">
              {t('barbers.label')}
            </legend>
            {barbers.length === 0 ? (
              <p className="text-sm text-warning">{t('barbers.noneAvailable')}</p>
            ) : (
              <BarberSelect
                barbers={barbers}
                value={barberId}
                onChange={setBarberId}
                nextAvailableLabel={t('barbers.nextAvailable')}
                labelledBy="barber-label-new"
              />
            )}
          </fieldset>

          <label className="flex items-start gap-3 text-sm text-fg-muted">
            <input
              type="checkbox"
              className="mt-1 h-5 w-5 touch-target"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
            />
            {t('checkIn.marketingConsentLabel')}
          </label>

          <HoneypotField value={honeypot} onChange={setHoneypot} />

          {submitError && <ErrorBanner>{submitError}</ErrorBanner>}

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? <Spinner /> : t('checkIn.submitButton')}
          </button>
          <button
            type="button"
            className="text-sm text-primary underline"
            onClick={() => setStep('phone')}
          >
            {t('common.back')}
          </button>
        </form>
      )}
    </div>
  );
});

/** Honeypot: real users never see or fill this in; a filled value marks the submission
 * as bot traffic server-side without showing the visitor a CAPTCHA. */
function HoneypotField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: '-9999px',
        top: 'auto',
        width: 1,
        height: 1,
        overflow: 'hidden',
      }}
      aria-hidden="true"
    >
      <label htmlFor="hp_confirm_2x">Leave this field blank</label>
      <input
        id="hp_confirm_2x"
        name="hp_confirm_2x"
        tabIndex={-1}
        autoComplete="off"
        data-lpignore="true"
        data-1p-ignore="true"
        data-form-type="other"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
